/**
 * Shared hook for settings actions (sign out, theme, manage, version)
 *
 * Extracted from HeaderPopoverContainer so both the header popover (mobile)
 * and SidePanel (web sidebar) can consume the same settings data and actions
 * without duplicating sign-out flows, theme cycling, or manage navigation.
 * @module hooks/useSettingsActions
 */

import * as Application from "expo-application";
import * as Device from "expo-device";
import { type Href, useRouter } from "expo-router";
import * as Updates from "expo-updates";
import {
  LanguagesIcon,
  MonitorIcon,
  MoonIcon,
  PaletteIcon,
  SunIcon,
} from "lucide-react-native";
import React, { useCallback, useMemo } from "react";
import { Alert, Platform } from "react-native";

import buildVersionString from "@/utils/buildVersionString";

import { version } from "../../package.json";

import { AnalyticsTracker } from "@/features/analytics";
import { useAuth, useCurrentUser } from "@/features/auth";
import { RAW_EXPO_PUBLIC_ENV } from "@/lib/env";
import {
  InvitationStatus,
  type SupportedLocale,
  useAuthDeactivateMeMutation,
  useAuthSignOutAllDevicesMutation,
  useAuthSignOutMutation,
  useAuthUpdateMyProfileMutation,
  useInvitationsListMyOrganizationsQuery,
  useInvitationsListMyReceivedInvitationsQuery,
  usePlayerDetailSupportedLocalesQuery,
} from "@/generated/graphql";
import {
  getLanguageLabel,
  narrowToSupportedUiLocales,
  resolveDisplayLocale,
} from "@/constants/locales";

import useRecordUserGoal, { GoalType } from "@/hooks/useRecordUserGoal";
import { useApolloClient } from "@/providers/ApolloClient";
import { useTheme } from "@/providers/ThemeProvider";
import { loadingPageRef } from "@/utils/loadingPageRef";
import { ifWeb } from "@/utils/platform";
import { resetPerUserState } from "@/stores/resetPerUserState";

/**
 * Pre-built press handlers for each manage route
 */
interface ManageItemHandlers {
  onPlayerRoster: () => void;
  onUsers: () => void;
  onInvitations: () => void;
  onOrganizations: () => void;
  onReceivedInvitations: () => void;
  onNotificationSettings: () => void;
}

/**
 * Return type for useSettingsActions hook
 */
interface UseSettingsActionsReturn {
  /** Current organization name */
  readonly organizationName: string;
  /** Whether the current user is an admin */
  readonly isAdmin: boolean;
  /** Number of valid organizations the user belongs to */
  readonly numberOfValidOrganizations: number;
  /** Whether the user has pending invitations */
  readonly hasPendingInvitations: boolean;
  /** Current theme mode */
  readonly mode: "light" | "dark" | "system";
  /** Icon component for the current theme */
  readonly themeIcon: React.ElementType;
  /** Label for the current theme (e.g., "Theme: Dark") */
  readonly themeLabel: string;
  /** Handler to cycle through theme modes */
  readonly onThemeToggle: () => void;
  /** Formatted version string for display */
  readonly versionString: string;
  /** Handler for version press (copies debug info on web, shows alert on native) */
  readonly onVersionPress: () => void;
  /** Handler for sign out of the current device only (the default) */
  readonly onSignOut: () => Promise<void>;
  /** Handler for sign out of all devices (with confirmation prompt) */
  readonly onSignOutAllDevices: () => Promise<void>;
  /** Handler for account deactivation */
  readonly onDeactivateMe: () => Promise<void>;
  /** Pre-built handlers for manage navigation routes */
  readonly manageItemHandlers: ManageItemHandlers;
  /**
   * Label for the app-wide Language row.
   *
   * `"Language: English"` once the profile has loaded; the bare `"Language"`
   * while it has not. Those are different states and the row must not collapse
   * them — asserting "English" before anything is known would be a claim the
   * hook cannot support, and it is exactly the value a stranded profile
   * resolves to, so the two would be indistinguishable.
   */
  readonly languageLabel: string;
  /** Icon component for the Language row */
  readonly languageIcon: React.ElementType;
  /**
   * The offered locale the profile currently resolves to, or `undefined` while
   * the profile has not loaded — the value the options list marks as selected.
   */
  readonly selectedLanguageCode: string | undefined;
  /** The offered language options, code + display name, in canonical order */
  readonly languageOptions: readonly { code: string; label: string }[];
  /** Handler to select a language */
  readonly onLanguageSelect: (localeCode: string) => void;
}

/**
 * Navigates on web via History API pushState + popstate to bypass
 * nested Stack navigator interception (SE-3726).
 * @param path - Absolute route path (e.g. "/users" or "/organizations")
 */
const webNavigate = (path: string): void => {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
};

/**
 * Provides settings data and actions for the header popover and side panel
 *
 * Encapsulates sign-out flow (with analytics, cache clearing, and reactive
 * var reset), theme cycling (light→dark→system), manage navigation with
 * SE-3726 web workaround, and version info display.
 * @param onClose - Callback to close the parent popover/panel after actions
 * @returns Settings data (org, admin, theme, version) and action handlers
 * @remarks Sign-out is best-effort on the backend (SE-4047) — local state
 * clearing and navigation proceed even if the mutation fails.
 */
function useSettingsActions(onClose: () => void): UseSettingsActionsReturn {
  const auth = useAuth();
  const router = useRouter();
  const client = useApolloClient();
  const currentUser = useCurrentUser();
  const { mode, setMode } = useTheme();
  const recordDeactivateMeGoal = useRecordUserGoal({
    goalType: GoalType.DeactivateMe,
    value: currentUser?.id ?? "",
  });
  const recordSignOutGoal = useRecordUserGoal({
    goalType: GoalType.SignOut,
    value: currentUser?.id ?? "",
  });

  const organizationName = useMemo(
    () => currentUser?.organization?.name ?? "",
    [currentUser?.organization?.name]
  );

  const isSignedOut = !auth?.data?.accessToken;
  const { data } = useInvitationsListMyOrganizationsQuery({
    skip: isSignedOut,
  });
  const { data: invitationData } = useInvitationsListMyReceivedInvitationsQuery(
    {
      // SE-8573: `skip` alone does not stop an already-armed Apollo poll — a
      // token cleared while a poll was in flight kept it polling signed-out.
      // Zeroing the interval tears the timer down.
      pollInterval: isSignedOut ? 0 : 30000,
      variables: { status: InvitationStatus.Pending },
      skip: isSignedOut,
    }
  );

  const [deactivateMe] = useAuthDeactivateMeMutation({
    onCompleted: () => {
      recordDeactivateMeGoal();
    },
  });
  const [signOutAccount] = useAuthSignOutMutation({
    onCompleted: () => {
      recordSignOutGoal();
    },
  });
  const [signOutAllDevicesMutation] = useAuthSignOutAllDevicesMutation({
    onCompleted: () => {
      recordSignOutGoal();
    },
  });
  const [updateMyProfile] = useAuthUpdateMyProfileMutation();
  /*
   * SE-6509: unskipped. This query used to be gated on `VOICE_DICTATION`
   * because it only fed the dictation row; the row is now the app-wide
   * Language control, which a dictation flag must not be able to switch off.
   * The flag itself and its other consumer (player-intake) are untouched.
   */
  const { data: localesData } = usePlayerDetailSupportedLocalesQuery();
  /*
   * The backend query keeps returning all twenty SupportedLocale values; the
   * UI offers six. Narrow here rather than rendering the result raw, so the
   * enum needs no migration and adding locale #7 is a one-line change in
   * `@/constants/locales` (SE-6505). A failed, skipped or unresolved query
   * still yields the full six.
   */
  const offeredLocales = useMemo(
    () => narrowToSupportedUiLocales(localesData?.supportedLocales),
    [localesData?.supportedLocales]
  );

  const numberOfValidOrganizations = (data?.listMyOrganizations?.edges ?? [])
    .length;

  const hasPendingInvitations =
    (invitationData?.listMyReceivedInvitations?.edges ?? []).length > 0;

  /*
   * SE-8076: the body of this moved to `resetPerUserState` so the token-expiry
   * path can run the same set. It used to be reachable only through
   * `finalizeSignOut`, which exists only in this hook — so expiry, which signs
   * out from `app/(root)/_layout.tsx` without touching this file, ran none of
   * it and handed the next user the previous one's state.
   */
  const handleResetVar = useCallback(() => {
    resetPerUserState();
  }, []);

  const finalizeSignOut = useCallback(
    async (reason: "sign_out_single_device" | "sign_out_all_devices") => {
      // SE-5225: capture the sign-out BEFORE resetting the PostHog identity.
      // resetIdentity() clears the distinct_id, so logging after it attributed
      // every event to an anonymous user ($is_identified=false) — 62/62 prod
      // sign-outs in 30d landed unidentified. Awaiting the (async) logger
      // guarantees capture() runs while the user identity is still active.
      await AnalyticsTracker.user.logUserSignOut({
        user_id: currentUser?.id ?? "",
        organization_id: currentUser?.organization?.id ?? "",
        phone_number: currentUser?.phoneNumber ?? "",
        reason,
        device_name: Device?.modelName ?? "",
        device_os: Device?.osName ?? "",
        device_type: ifWeb("web", "mobile"),
        platform: Platform.OS,
        timestamp: new Date().toISOString(),
      });
      AnalyticsTracker.resetIdentity();

      await Promise.all([
        auth?.onCompleteSignOut(),
        handleResetVar(),
        client?.client.clearStore().catch(() => undefined),
      ]);

      onClose();
      router.navigate("/signin");
    },
    [auth, client, currentUser, onClose, handleResetVar, router]
  );

  const handleSignOut = useCallback(async () => {
    const refreshToken = auth?.data?.refreshToken;
    if (refreshToken) {
      signOutAccount({ variables: { input: { refreshToken } } }).catch(
        error => {
          console.warn("Backend sign-out mutation failed:", error);
        }
      );
    } else {
      console.warn(
        "No refresh token available; skipping backend sign-out revoke."
      );
    }

    await finalizeSignOut("sign_out_single_device");
  }, [auth?.data?.refreshToken, finalizeSignOut, signOutAccount]);

  const handleSignOutAllDevices = useCallback(async () => {
    const confirmMessage =
      "Sign out of every device? This will end your session on all phones, tablets, and computers where you're signed in.";

    const runSignOut = async () => {
      signOutAllDevicesMutation().catch(error => {
        console.warn("Backend sign-out-all-devices mutation failed:", error);
      });
      await finalizeSignOut("sign_out_all_devices");
    };

    if (Platform.OS === "web") {
      const confirmed = window.confirm(confirmMessage);
      if (confirmed) {
        await runSignOut();
      }
      return;
    }

    Alert.alert("Sign Out of All Devices", confirmMessage, [
      { text: "Cancel", style: "cancel" },
      {
        text: "Sign Out Everywhere",
        style: "destructive",
        onPress: () => {
          runSignOut().catch(error => {
            console.warn("Sign out of all devices flow failed:", error);
          });
        },
      },
    ]);
  }, [finalizeSignOut, signOutAllDevicesMutation]);

  const handleDeactivateMe = useCallback(async () => {
    if (Platform.OS === "web") {
      const doDeactivate = window.confirm(
        `Are you sure you want to deactivate your account?`
      );
      if (doDeactivate) {
        try {
          loadingPageRef.current?.showLoading();
          await deactivateMe();
          await handleSignOut();
        } catch (_error) {
          console.warn("Deactivation failed:", _error);
        }
        loadingPageRef.current?.hideLoading();
      }
    } else {
      Alert.alert(
        `Deactivate Your Account`,
        `Are you sure you want to deactivate your account?`,
        [
          {
            text: "Cancel",
            style: "cancel",
          },
          {
            text: "Yes",
            onPress: async () => {
              try {
                loadingPageRef.current?.showLoading();
                await deactivateMe();
                await handleSignOut();
              } catch (_error) {
                console.warn("Deactivation failed:", _error);
              }
              loadingPageRef.current?.hideLoading();
            },
            style: "destructive",
          },
        ]
      );
    }
  }, [deactivateMe, handleSignOut]);

  /**
   * Navigates to a manage section route and closes the panel/popover.
   * On web, uses History API to bypass nested Stack interception (SE-3726).
   */
  const handleManageNavigate = useCallback(
    (path: string) => {
      onClose();
      if (Platform.OS === "web") {
        webNavigate(path);
      } else {
        router.navigate(path as Href);
      }
    },
    [onClose, router]
  );

  const manageItemHandlers = useMemo(
    () => ({
      onPlayerRoster: () => handleManageNavigate("/player-roster"),
      onUsers: () => handleManageNavigate("/users"),
      onInvitations: () => handleManageNavigate("/invitations"),
      onOrganizations: () => handleManageNavigate("/organizations"),
      onReceivedInvitations: () =>
        handleManageNavigate("/received-invitations"),
      onNotificationSettings: () =>
        handleManageNavigate("/notification-settings"),
    }),
    [handleManageNavigate]
  );

  const THEME_ICONS: Record<string, React.ElementType> = useMemo(
    () => ({
      light: SunIcon,
      dark: MoonIcon,
      system: MonitorIcon,
    }),
    []
  );

  const themeIcon = THEME_ICONS[mode] ?? PaletteIcon;

  const themeLabel = useMemo(() => {
    const labelsByMode: Record<string, string> = {
      light: "Theme: Light",
      dark: "Theme: Dark",
      system: "Theme: System",
    };
    return labelsByMode[mode] ?? "Theme";
  }, [mode]);

  const onThemeToggle = useCallback(() => {
    const nextMode =
      mode === "light" ? "dark" : mode === "dark" ? "system" : "light";
    setMode(nextMode);
  }, [mode, setMode]);

  const versionString = useMemo(
    () =>
      buildVersionString({
        version,
        // SE-7834: the RAW value. This string is shown to the user and copied
        // into bug reports, so it must say what the build said — a build with
        // no configuration should read blank, not "production".
        environment: RAW_EXPO_PUBLIC_ENV,
        runtimeVersion: Updates.runtimeVersion,
        nativeBuildVersion: Application.nativeBuildVersion,
        channel: Updates.channel,
      }),
    []
  );

  const otaDebugInfo = useMemo(
    () => ({
      isEnabled: Updates.isEnabled,
      channel: Updates.channel,
      runtimeVersion: Updates.runtimeVersion,
      isEmbeddedLaunch: Updates.isEmbeddedLaunch,
      updateId: Updates.updateId,
    }),
    []
  );

  const onVersionPress = useCallback(() => {
    const debugText = `v${version} ${RAW_EXPO_PUBLIC_ENV ?? ""} ${Updates.runtimeVersion ?? "unknown"} Build: ${Application.nativeBuildVersion ?? ""}`;

    if (Platform.OS === "web") {
      navigator.clipboard.writeText(debugText);
    } else {
      Alert.alert(
        "OTA Debug Info",
        `Version: ${debugText}\n\nisEnabled: ${otaDebugInfo.isEnabled}\nchannel: ${otaDebugInfo.channel ?? "null"}\nruntimeVersion: ${otaDebugInfo.runtimeVersion ?? "null"}\nisEmbeddedLaunch: ${otaDebugInfo.isEmbeddedLaunch}\nupdateId: ${otaDebugInfo.updateId ?? "null"}`
      );
    }
  }, [otaDebugInfo]);

  /*
   * `languagePreference` is NOT NULL and live users sit on the fourteen
   * dropped locales, so the value resolves through the display-fallback chain
   * (`ja_JP` reads as English, `pt_PT` as Portuguese). Resolution only —
   * nothing is written back to the profile, and merely opening the drawer
   * issues no mutation. SE-6509 made `useDictation` resolve by the same rule,
   * so this label is now true of the microphone as well as of the screen.
   *
   * `undefined` while the profile is still loading: an unloaded profile is not
   * an English one, and the row says so by omitting the value entirely.
   */
  const selectedLanguageCode = useMemo(
    () =>
      currentUser
        ? resolveDisplayLocale(currentUser.languagePreference)
        : undefined,
    [currentUser]
  );

  const languageLabel = useMemo(
    () =>
      selectedLanguageCode
        ? `Language: ${getLanguageLabel(selectedLanguageCode)}`
        : "Language",
    [selectedLanguageCode]
  );

  const languageOptions = useMemo(
    () =>
      offeredLocales.map(code => ({
        code,
        label: getLanguageLabel(code),
      })),
    [offeredLocales]
  );

  const onLanguageSelect = useCallback(
    (localeCode: string) => {
      updateMyProfile({
        variables: {
          input: { languagePreference: localeCode as SupportedLocale },
        },
        refetchQueries: ["AuthMe"],
      }).catch((error: unknown) => {
        console.warn("Failed to update language preference:", error);
      });
    },
    [updateMyProfile]
  );

  return {
    organizationName,
    isAdmin: currentUser?.isAdmin ?? false,
    numberOfValidOrganizations,
    hasPendingInvitations,
    mode,
    themeIcon,
    themeLabel,
    onThemeToggle,
    versionString,
    onVersionPress,
    onSignOut: handleSignOut,
    onSignOutAllDevices: handleSignOutAllDevices,
    onDeactivateMe: handleDeactivateMe,
    manageItemHandlers,
    languageLabel,
    languageIcon: LanguagesIcon,
    selectedLanguageCode,
    languageOptions,
    onLanguageSelect,
  };
}

export default useSettingsActions;
export type { ManageItemHandlers, UseSettingsActionsReturn };
